import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { getChatActions } from '@/lib/chat-actions/chat-actions-bindings';
import { mapAudienceProfileToSpark } from '@/entities/persona-spark';
import { createOpportunitySummary } from '@/entities/opportunity';
import type { SocialOpportunityItem } from '@inference.engine/widgets/web';
import { SPECIAL_AGENT_IDS } from '@/entities/agent';
import { usePermissions } from '@/entities/user';
import { useBackTo } from '@/shared/lib/back-to';
import type { InterviewMetadata, AudienceProfile } from './useStartInterview.types';

export const useStartInterview = () => {
  const navigate = useNavigate();
  const backTo = useBackTo();
  const { canOpenTool } = usePermissions();

  const startInterview = useCallback(
    (
      personaId: string,
      promptOverride?: string,
      audienceProfile?: AudienceProfile,
      opportunity?: SocialOpportunityItem,
    ) => {
      const metadata: InterviewMetadata = {
        ...(audienceProfile && { personaSpark: mapAudienceProfileToSpark(audienceProfile) }),
        ...(opportunity && { opportunitySummary: createOpportunitySummary(opportunity) }),
      };

      const hasContext = audienceProfile && opportunity;

      const { launchAgentWithWidget, sendOrCreateChat } = getChatActions();

      if (hasContext && promptOverride) {
        void sendOrCreateChat(navigate, {
          agentId: SPECIAL_AGENT_IDS.AUDIENCE_FLOW,
          prompt: promptOverride,
          backTo,
          inputs: {
            selectedPersonaId: personaId,
            promptOverride,
            metadata,
          },
        });
        return;
      }

      launchAgentWithWidget(navigate, SPECIAL_AGENT_IDS.AUDIENCE_FLOW, {
        inputs: {
          selectedPersonaId: personaId,
          ...(promptOverride && { promptOverride }),
          ...(Object.keys(metadata).length > 0 && { metadata }),
        },
        backTo,
      });
    },
    [navigate, backTo],
  );

  // Without the open permission there is no handler, and the spark card drops its "start chat" CTA.
  return {
    startInterview: canOpenTool(SPECIAL_AGENT_IDS.AUDIENCE_FLOW) ? startInterview : undefined,
  };
};
